/**
 * Default upper bound for an image's longest dimension. Images sourced from
 * modern phones routinely exceed 4000px on the long side, which is wasteful
 * to upload and renders far larger than any container in the app would ever
 * need. 1200 px is the largest viewport width we care about and yields a
 * file size in the low hundreds of KB for typical photos at 0.85 quality.
 */
export const DEFAULT_MAX_IMAGE_DIMENSION = 1200

export interface ResizeImageOptions {
  maxDimension?: number
  /** JPEG quality for re-encoded output, 0..1. Ignored for PNGs. */
  quality?: number
  /**
   * Force the output MIME type instead of preserving the input's. Set this when
   * the destination object has a fixed extension and content type: an image
   * already within `maxDimension` is then still re-encoded, because returning
   * the original file would store the wrong format under that extension.
   */
  outputType?: string
}

/**
 * Shrink an image so its longest side is at most `maxDimension` pixels.
 * Returns a new Blob in the same MIME type as the input (falling back to
 * JPEG when the original type isn't a supported canvas export format).
 *
 * If the image is already within bounds and no `outputType` is forced (or
 * the forced type already matches the input), the original File is returned
 * unchanged — no re-encoding loss.
 */
export async function resizeImage(
  file: File,
  options: ResizeImageOptions = {},
): Promise<Blob> {
  const maxDimension = options.maxDimension ?? DEFAULT_MAX_IMAGE_DIMENSION
  const quality = options.quality ?? 0.85

  const bitmap = await createImageBitmapSafe(file)
  try {
    const outputType =
      options.outputType ?? (isCanvasMimeType(file.type) ? file.type : 'image/jpeg')
    const longest = Math.max(bitmap.width, bitmap.height)
    const withinBounds = longest <= maxDimension

    // Gate on the caller's raw outputType, not the derived value above: when no
    // outputType was requested, the derived value still defaults to 'image/jpeg'
    // for a non-canvas type (GIF, TIFF, Safari-decoded HEIC, ...), and comparing
    // against that default would push an already-small file through the canvas
    // and flatten it to a static JPEG. The short-circuit is skipped only when
    // the caller asked for a specific type that the file doesn't already carry.
    if (withinBounds && (!options.outputType || file.type === options.outputType)) {
      return file
    }

    const scale = withinBounds ? 1 : maxDimension / longest
    const width = Math.round(bitmap.width * scale)
    const height = Math.round(bitmap.height * scale)

    const canvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(width, height)
        : (() => {
            const c = document.createElement('canvas')
            c.width = width
            c.height = height
            return c
          })()
    const ctx = (canvas as HTMLCanvasElement | OffscreenCanvas).getContext('2d') as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null
    if (!ctx) {
      // Defensive: no 2d context available (e.g. an exotic jsdom setup). With no
      // forced type, hand back the original so the upload still succeeds at full
      // size. With one, the caller's destination requires that exact format, and
      // silently uploading the wrong one is worse than a visible failure.
      if (options.outputType && file.type !== options.outputType) {
        throw new Error('resizeImage: no 2d context available to encode ' + options.outputType)
      }
      return file
    }
    ctx.drawImage(bitmap, 0, 0, width, height)

    return await canvasToBlob(canvas as HTMLCanvasElement | OffscreenCanvas, outputType, quality)
  } finally {
    if (typeof bitmap.close === 'function') bitmap.close()
  }
}

async function createImageBitmapSafe(file: File): Promise<ImageBitmap> {
  // createImageBitmap is the modern path; older browsers and jsdom need a
  // fallback via HTMLImageElement. We don't ship to those browsers, but
  // jsdom tests use the HTMLImageElement path.
  if (typeof createImageBitmap === 'function') {
    return await createImageBitmap(file)
  }
  return await new Promise<ImageBitmap>((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img as unknown as ImageBitmap)
    img.onerror = reject
    img.src = URL.createObjectURL(file)
  })
}

function isCanvasMimeType(type: string): boolean {
  return type === 'image/png' || type === 'image/jpeg' || type === 'image/webp'
}

function canvasToBlob(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  type: string,
  quality: number,
): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return (canvas as OffscreenCanvas).convertToBlob({ type, quality })
  }
  return new Promise<Blob>((resolve, reject) => {
    ;(canvas as HTMLCanvasElement).toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob returned null'))),
      type,
      quality,
    )
  })
}
